import {
    BuyerInvoiceStatus,
    FundingBatchDetailStatus,
    InvoiceBuyerApprovalStatus,
    InvoiceFundingStatus
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    BusinessUnitSortColumn,
    BuyerInvoiceStatus2,
    EarlyPaymentStatus2,
    EnumInput,
    InvoiceFundingStatus2,
    PayableStatus,
    PayableStatusLegacy,
    TransferTypeForSupplier
} from '../model/models';

export const PAY_LATER_STATUSES: PayableStatus[] = [
    PayableStatus.PAY_LATER_REQUESTED,
    PayableStatus.PAY_LATER,
    PayableStatus.PAY_LATER_CLOSED,
    PayableStatus.EARLY_PAID_PAY_LATER,
    PayableStatus.EARLY_PAID_PAY_LATER_CLOSED
];

export const EARLY_PAID_STATUSES: PayableStatus[] = [
    PayableStatus.EARLY_PAID,
    PayableStatus.EARLY_PAID_CLOSED,
    PayableStatus.EARLY_PAID_PAY_LATER,
    PayableStatus.EARLY_PAID_PAY_LATER_CLOSED
];

export function mapPayableStatusLegacyToInvoiceFundingStatus(
    statusLegacy: PayableStatusLegacy
): InvoiceFundingStatus2 {
    switch (statusLegacy) {
        case PayableStatusLegacy.ELIGIBLE:
            return InvoiceFundingStatus2.ELIGIBLE;
        case PayableStatusLegacy.INELIGIBLE:
            return InvoiceFundingStatus2.INELIGIBLE;
        case PayableStatusLegacy.EARLY_PAYMENT_SCHEDULED:
            return InvoiceFundingStatus2.SCHEDULED;
        case PayableStatusLegacy.PENDING:
            return InvoiceFundingStatus2.PENDING;
        case PayableStatusLegacy.EARLY_PAID:
            return InvoiceFundingStatus2.EARLY_PAID;
        case PayableStatusLegacy.PAID:
            return InvoiceFundingStatus2.PAID;
        case PayableStatusLegacy.FUNDING_REJECTED:
            return InvoiceFundingStatus2.FUNDING_REJECTED;
        case PayableStatusLegacy.REVOKED:
            return InvoiceFundingStatus2.REVOKED;
        case PayableStatusLegacy.EARLY_PAID_CLOSED:
            return InvoiceFundingStatus2.EARLY_PAID_CLOSED;
        case PayableStatusLegacy.CLOSED:
            return InvoiceFundingStatus2.CLOSED;
        case PayableStatusLegacy.PAY_LATER:
            return InvoiceFundingStatus2.PAY_LATER;
        default:
            return InvoiceFundingStatus2.UNKNOWN_STATUS;
    }
}

export function mapPayableStatusLegacyToEarlyPmtStatus(
    statusLegacy: PayableStatusLegacy
): EarlyPaymentStatus2 {
    switch (statusLegacy) {
        case PayableStatusLegacy.INELIGIBLE:
            return EarlyPaymentStatus2.EP_REJECTED;
        case PayableStatusLegacy.EARLY_PAYMENT_SCHEDULED:
            return EarlyPaymentStatus2.EP_SCHEDULED;
        case PayableStatusLegacy.PENDING:
            return EarlyPaymentStatus2.EP_PENDING;
        case PayableStatusLegacy.EARLY_PAID:
            return EarlyPaymentStatus2.EP_FUNDED;
        case PayableStatusLegacy.PAID:
            return EarlyPaymentStatus2.EP_FUNDED_TERMS;
        case PayableStatusLegacy.FUNDING_REJECTED:
            return EarlyPaymentStatus2.EP_REJECTED;
        case PayableStatusLegacy.REVOKED:
            return EarlyPaymentStatus2.EP_REJECTED;
        case PayableStatusLegacy.EARLY_PAID_CLOSED:
            return EarlyPaymentStatus2.EP_FUNDED;
        case PayableStatusLegacy.CLOSED:
            return EarlyPaymentStatus2.EP_FUNDED_TERMS;
        case PayableStatusLegacy.PAY_LATER:
            return EarlyPaymentStatus2.EP_FUNDED_TERMS_PAY_LATER;
        default:
            return EarlyPaymentStatus2.UNKNOWN_PAYMENT_STATUS;
    }
}

export function mapPayableStatusToBuyerApprovalStatus(
    status: PayableStatus
): InvoiceBuyerApprovalStatus {
    // TODO: Implement IN_REVIEW
    switch (status) {
        case PayableStatus.APPROVED:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.REJECTED:
            return InvoiceBuyerApprovalStatus.REJECTED;
        case PayableStatus.CLOSED:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.EARLY_PAY_REQUESTED:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.EARLY_PAID:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.EARLY_PAID_CLOSED:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.EARLY_PAID_PAY_LATER:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.EARLY_PAID_PAY_LATER_CLOSED:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.PAY_LATER:
            return InvoiceBuyerApprovalStatus.APPROVED;
        case PayableStatus.PAY_LATER_CLOSED:
            return InvoiceBuyerApprovalStatus.APPROVED;
        default:
            return InvoiceBuyerApprovalStatus.IN_REVIEW;
    }
}

export function mapBuyerStatusFiltersToPayableLegacyStatusInput(
    buyerInvoiceStatus: (BuyerInvoiceStatus | BuyerInvoiceStatus2)[] = []
): EnumInput<PayableStatusLegacy>[] | undefined {
    const statusFilter = new Set<PayableStatusLegacy>();
    buyerInvoiceStatus?.forEach((status) => {
        switch (status) {
            case BuyerInvoiceStatus2.ELIGIBLE:
                statusFilter.add(PayableStatusLegacy.ELIGIBLE);
                break;
            case BuyerInvoiceStatus2.EARLY_PAID:
                statusFilter.add(PayableStatusLegacy.EARLY_PAID);
                break;
            case BuyerInvoiceStatus2.EARLY_PAID_CLOSED:
                statusFilter.add(PayableStatusLegacy.EARLY_PAID_CLOSED);
                // TODO: Add PAY_LATER_CLOSED to buyer filter options?
                // statusFilter.add(PayableStatusLegacy.PAY_LATER_CLOSED);
                break;
            case BuyerInvoiceStatus2.CLOSED:
                statusFilter.add(PayableStatusLegacy.CLOSED);
                break;
            case BuyerInvoiceStatus2.INELIGIBLE:
                statusFilter.add(PayableStatusLegacy.INELIGIBLE);
                break;
            case BuyerInvoiceStatus2.REVOKED:
                statusFilter.add(PayableStatusLegacy.REVOKED);
                break;
            case BuyerInvoiceStatus2.PENDING:
                statusFilter.add(PayableStatusLegacy.PENDING);
                break;
            case BuyerInvoiceStatus2.PAY_LATER:
                statusFilter.add(PayableStatusLegacy.PAY_LATER);
                break;
            case BuyerInvoiceStatus2.EARLY_PAYMENT_SCHEDULED:
                statusFilter.add(PayableStatusLegacy.EARLY_PAYMENT_SCHEDULED);
                break;
            case BuyerInvoiceStatus2.PAID:
                statusFilter.add(PayableStatusLegacy.PAID);
                break;
        }
    });

    if (statusFilter.size === 0) {
        return undefined;
    }

    return Array.from(statusFilter).map((status) => new EnumInput(status));
}

export function mapInvoiceFundingStatusToLegacyStatus(
    invFundingStatuses: (InvoiceFundingStatus | InvoiceFundingStatus2)[] = []
): EnumInput<PayableStatusLegacy>[] | undefined {
    const statusFilter = new Set<EnumInput<PayableStatusLegacy>>();

    invFundingStatuses?.forEach((status) => {
        switch (status) {
            case InvoiceFundingStatus2.UNFUNDED:
            case InvoiceFundingStatus2.SENT:
            case InvoiceFundingStatus2.ELIGIBLE:
                statusFilter.add(new EnumInput(PayableStatusLegacy.ELIGIBLE));
                break;
            case InvoiceFundingStatus2.PENDING:
                statusFilter.add(new EnumInput(PayableStatusLegacy.PENDING));
                break;
            case InvoiceFundingStatus2.FUNDED:
            case InvoiceFundingStatus2.EARLY_PAID:
                statusFilter.add(new EnumInput(PayableStatusLegacy.EARLY_PAID));
                statusFilter.add(new EnumInput(PayableStatusLegacy.EARLY_PAID_CLOSED));
                break;
            case InvoiceFundingStatus2.CLOSED:
                statusFilter.add(new EnumInput(PayableStatusLegacy.CLOSED));
                statusFilter.add(new EnumInput(PayableStatusLegacy.EARLY_PAID_CLOSED));
                break;
            case InvoiceFundingStatus2.WILL_NOT_FUND:
            case InvoiceFundingStatus2.INELIGIBLE:
            case InvoiceFundingStatus2.FUNDING_REJECTED:
            case InvoiceFundingStatus2.REVOKED:
                statusFilter.add(new EnumInput(PayableStatusLegacy.REVOKED));
                statusFilter.add(new EnumInput(PayableStatusLegacy.FUNDING_REJECTED));
                statusFilter.add(new EnumInput(PayableStatusLegacy.INELIGIBLE));
                break;
            case InvoiceFundingStatus2.EARLY_PAID_CLOSED:
                statusFilter.add(new EnumInput(PayableStatusLegacy.EARLY_PAID_CLOSED));
                break;
            case InvoiceFundingStatus2.PAID:
                statusFilter.add(new EnumInput(PayableStatusLegacy.PAID));
                statusFilter.add(new EnumInput(PayableStatusLegacy.CLOSED));
                statusFilter.add(new EnumInput(PayableStatusLegacy.PAY_LATER));
                statusFilter.add(new EnumInput(PayableStatusLegacy.PAY_LATER_CLOSED));
                break;
            case InvoiceFundingStatus2.SCHEDULED:
                statusFilter.add(new EnumInput(PayableStatusLegacy.EARLY_PAYMENT_SCHEDULED));
                break;
        }
    });

    return Array.from(statusFilter);
}

export function mapLegacyStatusTransferType(
    legacyStatuses: FundingBatchDetailStatus[]
): EnumInput<TransferTypeForSupplier>[] {
    const statusFilter = new Set<TransferTypeForSupplier>();
    legacyStatuses?.forEach((legacyStatus) => {
        switch (legacyStatus) {
            case FundingBatchDetailStatus.REJECTED:
            case FundingBatchDetailStatus.CANCELLED:
                statusFilter.add(TransferTypeForSupplier.REJECTED);
                break;
            case FundingBatchDetailStatus.SENT:
                statusFilter.add(TransferTypeForSupplier.EARLY_PAYMENT);
                break;
            case FundingBatchDetailStatus.SCHEDULED:
                statusFilter.add(TransferTypeForSupplier.SCHEDULED_EARLY_PAYMENT);
                break;
            case FundingBatchDetailStatus.PAID:
                statusFilter.add(TransferTypeForSupplier.PAYMENT);
                break;
            case FundingBatchDetailStatus.UNKNOWN_FUNDING_STATUS:
            case FundingBatchDetailStatus.STAGED:
            case FundingBatchDetailStatus.IN_TRANSFER:
            case FundingBatchDetailStatus.APPROVED:
                statusFilter.add(TransferTypeForSupplier.PENDING);
                break;
            default:
                statusFilter.add(TransferTypeForSupplier.PENDING);
                break;
        }
    });

    return Array.from(statusFilter).map((status) => new EnumInput(status));
}

export function mapSupplierTransferTypeToLegacyStatus(
    transferTypeForSupplier: TransferTypeForSupplier
): FundingBatchDetailStatus {
    switch (transferTypeForSupplier) {
        case TransferTypeForSupplier.REJECTED:
            return FundingBatchDetailStatus.REJECTED;
        case TransferTypeForSupplier.EARLY_PAYMENT:
            return FundingBatchDetailStatus.SENT;
        case TransferTypeForSupplier.SCHEDULED_EARLY_PAYMENT:
            return FundingBatchDetailStatus.SCHEDULED;
        case TransferTypeForSupplier.PAYMENT:
            return FundingBatchDetailStatus.PAID;
        case TransferTypeForSupplier.PENDING:
            return FundingBatchDetailStatus.IN_TRANSFER;
        default:
            return FundingBatchDetailStatus.UNKNOWN_FUNDING_STATUS;
    }
}

export function mapBUSortColumn(sortColumn: string): BusinessUnitSortColumn {
    switch (sortColumn) {
        case 'buyerId':
            return BusinessUnitSortColumn.BUYER_EXTERNAL_ID;
        case 'buyerName':
            return BusinessUnitSortColumn.BUSINESS_UNIT_NAME;
        case 'enrolledSuppliersCount':
            return BusinessUnitSortColumn.SUPPLIER_COUNT;
        case 'openInvoiceCount':
            return BusinessUnitSortColumn.OPEN_INVOICE_COUNT;
        case 'openInvoiceTotal':
            return BusinessUnitSortColumn.OPEN_INVOICE_AMOUNT;
        case 'totalEarlyPaid':
            return BusinessUnitSortColumn.EARLY_PAID_AMOUNT;
        case 'percentUsed':
            return BusinessUnitSortColumn.USED_PERCENTAGE;
        case 'amountDue':
            return BusinessUnitSortColumn.AMOUNT_DUE;
        case 'totalBalance':
            return BusinessUnitSortColumn.TOTAL_BALANCE_AMOUNT;
    }
    return BusinessUnitSortColumn.BUSINESS_UNIT_NAME;
}
